import * as XLSX from 'xlsx';

const MAX_CATEGORY_CODE_LENGTH = 10;
const MAX_ACTIVITY_CODE_LENGTH = 50;

const HEADER_ALIASES = {
  categoryCode: ['categorycode', 'mastercategorycode'],
  categoryName: ['category', 'categoryname', 'mastercategory'],
  categoryDescription: ['categorydescription'],
  categoryOrder: ['categoryorder'],
  activityCode: ['activitycode', 'masteractivitycode'],
  activityName: ['activity', 'activityname', 'masteractivity'],
  activityDescription: ['activitydescription'],
  activityUnit: ['activityunit', 'defaultunit', 'quantityunit', 'qtyunit'],
  activityOrder: ['activityorder'],
  subTaskName: ['subtask', 'subtaskname', 'task', 'taskname', 'workitem'],
  subTaskDescription: ['subtaskdescription', 'taskdescription'],
  subTaskUnit: ['subtaskunit', 'taskunit', 'productivityunit'],
  defaultProductivity: [
    'defaultproductivity',
    'productivity',
    'manhours',
    'manhoursperunit',
    'manhourperunit',
  ],
  subTaskOrder: ['subtaskorder', 'taskorder'],
};

const normalizeText = (value) =>
  value === undefined || value === null ? '' : String(value).trim();

const normalizeHeader = (value) =>
  normalizeText(value).toLowerCase().replace(/[^a-z0-9]/g, '');

const normalizeKey = (value) => normalizeText(value).toLocaleLowerCase();

const slugify = (value) =>
  normalizeText(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const shortHash = (value, length) => {
  let hash = 2166136261;
  const text = normalizeKey(value);

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(36).toUpperCase().padStart(length, '0').slice(-length);
};

const generatedCategoryCode = (name) => {
  const words = slugify(name).split('-').filter(Boolean);
  const initials = words
    .map((word) => (word.length <= 3 ? word : word[0]))
    .join('')
    .slice(0, 5);
  const prefix = initials || 'CAT';
  return `${prefix}-${shortHash(name, 4)}`.slice(0, MAX_CATEGORY_CODE_LENGTH);
};

const generatedActivityCode = (categoryCode, name) => {
  const suffix = shortHash(`${categoryCode}:${name}`, 5);
  const readable = `${categoryCode}-${slugify(name) || 'ACT'}`;
  return `${readable.slice(0, MAX_ACTIVITY_CODE_LENGTH - suffix.length - 1)}-${suffix}`;
};

const parseOrder = (value, fallback, rowNumber, fieldName) => {
  if (value === undefined || value === null || normalizeText(value) === '') {
    return fallback;
  }

  const number = Number(value);
  if (!Number.isInteger(number) || number < 0) {
    throw new Error(`Row ${rowNumber}: ${fieldName} must be a non-negative whole number.`);
  }
  return number;
};

const parseProductivity = (value, rowNumber) => {
  const normalized =
    typeof value === 'string' ? value.replace(/,/g, '').trim() : value;
  const number = Number(normalized);

  if (normalized === '' || !Number.isFinite(number) || number < 0) {
    throw new Error(`Row ${rowNumber}: productivity/man-hours must be a non-negative number.`);
  }

  return number;
};

const findHeaderMapping = (rows) => {
  const maximumHeaderRows = Math.min(rows.length, 20);

  for (let rowIndex = 0; rowIndex < maximumHeaderRows; rowIndex += 1) {
    const headers = (rows[rowIndex] || []).map(normalizeHeader);
    const mapping = {};

    Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => {
      const columnIndex = headers.findIndex((header) => aliases.includes(header));
      if (columnIndex >= 0) mapping[field] = columnIndex;
    });

    if (
      mapping.categoryName !== undefined &&
      mapping.activityName !== undefined &&
      mapping.subTaskName !== undefined &&
      mapping.defaultProductivity !== undefined
    ) {
      return { mapping, dataStartRow: rowIndex + 1 };
    }
  }

  return null;
};

const findProductivityLayout = (rows) => {
  const dataStartRow = rows.findIndex(
    (row) =>
      normalizeText(row?.[0]) &&
      normalizeText(row?.[1]) &&
      normalizeText(row?.[7]) &&
      normalizeText(row?.[8]) &&
      row?.[9] !== undefined &&
      row?.[9] !== null,
  );

  if (dataStartRow < 0) return null;

  return {
    dataStartRow,
    mapping: {
      categoryName: 0,
      activityName: 1,
      activityUnit: 3,
      subTaskName: 7,
      subTaskUnit: 8,
      defaultProductivity: 9,
    },
  };
};

const cell = (row, mapping, field) => {
  const columnIndex = mapping[field];
  return columnIndex === undefined ? undefined : row[columnIndex];
};

const isBlankRow = (row) =>
  !row || row.every((value) => value === undefined || value === null || normalizeText(value) === '');

export const summarizeMasterData = (data) => {
  const categories = Array.isArray(data?.categories) ? data.categories : [];
  const activities = categories.reduce(
    (total, category) => total + (Array.isArray(category.activities) ? category.activities.length : 0),
    0,
  );
  const subTasks = categories.reduce(
    (total, category) =>
      total +
      (Array.isArray(category.activities)
        ? category.activities.reduce(
            (activityTotal, activity) =>
              activityTotal + (Array.isArray(activity.subTasks) ? activity.subTasks.length : 0),
            0,
          )
        : 0),
    0,
  );

  return { categories: categories.length, activities, subTasks };
};

export const parseMasterDataJson = (text) => {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('The selected JSON file is not valid JSON.');
  }

  if (!data || !Array.isArray(data.categories) || data.categories.length === 0) {
    throw new Error('The JSON data must contain a non-empty "categories" array.');
  }

  return data;
};

export const convertRowsToMasterData = (rows, sheetName = 'Sheet1') => {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error(`Worksheet "${sheetName}" is empty.`);
  }

  const layout = findHeaderMapping(rows) || findProductivityLayout(rows);
  if (!layout) {
    throw new Error(
      `Worksheet "${sheetName}" does not contain Category, Activity, Sub-task, Unit, and Productivity columns.`,
    );
  }

  const categories = [];
  const categoryMap = new Map();
  const categoryCodes = new Map();

  rows.slice(layout.dataStartRow).forEach((row, offset) => {
    if (isBlankRow(row)) return;

    const rowNumber = layout.dataStartRow + offset + 1;
    const categoryName = normalizeText(cell(row, layout.mapping, 'categoryName'));
    const activityName = normalizeText(cell(row, layout.mapping, 'activityName'));
    const subTaskName = normalizeText(cell(row, layout.mapping, 'subTaskName'));
    const subTaskUnit = normalizeText(cell(row, layout.mapping, 'subTaskUnit'));
    const activityUnit =
      normalizeText(cell(row, layout.mapping, 'activityUnit')) || subTaskUnit || 'No';

    if (!categoryName || !activityName || !subTaskName) {
      throw new Error(
        `Row ${rowNumber}: category, activity, and sub-task names are all required.`,
      );
    }

    const categoryKey = normalizeKey(categoryName);
    let category = categoryMap.get(categoryKey);

    if (!category) {
      const suppliedCode = slugify(cell(row, layout.mapping, 'categoryCode'));
      const categoryCode = suppliedCode || generatedCategoryCode(categoryName);
      const existingCategoryName = categoryCodes.get(categoryCode);

      if (categoryCode.length > MAX_CATEGORY_CODE_LENGTH) {
        throw new Error(
          `Row ${rowNumber}: category code "${categoryCode}" exceeds ${MAX_CATEGORY_CODE_LENGTH} characters.`,
        );
      }
      if (existingCategoryName && existingCategoryName !== categoryKey) {
        throw new Error(`Row ${rowNumber}: duplicate category code "${categoryCode}".`);
      }

      categoryCodes.set(categoryCode, categoryKey);
      category = {
        code: categoryCode,
        name: categoryName,
        description:
          normalizeText(cell(row, layout.mapping, 'categoryDescription')) || categoryName,
        order: parseOrder(
          cell(row, layout.mapping, 'categoryOrder'),
          categories.length + 1,
          rowNumber,
          'category order',
        ),
        activities: [],
        _activityMap: new Map(),
        _activityCodes: new Map(),
      };
      categories.push(category);
      categoryMap.set(categoryKey, category);
    }

    const activityKey = normalizeKey(activityName);
    let activity = category._activityMap.get(activityKey);

    if (!activity) {
      const suppliedCode = slugify(cell(row, layout.mapping, 'activityCode'));
      const activityCode = suppliedCode || generatedActivityCode(category.code, activityName);
      const existingActivityName = category._activityCodes.get(activityCode);

      if (activityCode.length > MAX_ACTIVITY_CODE_LENGTH) {
        throw new Error(
          `Row ${rowNumber}: activity code "${activityCode}" exceeds ${MAX_ACTIVITY_CODE_LENGTH} characters.`,
        );
      }
      if (existingActivityName && existingActivityName !== activityKey) {
        throw new Error(
          `Row ${rowNumber}: duplicate activity code "${activityCode}" in ${category.name}.`,
        );
      }

      category._activityCodes.set(activityCode, activityKey);
      activity = {
        code: activityCode,
        name: activityName,
        description:
          normalizeText(cell(row, layout.mapping, 'activityDescription')) || activityName,
        defaultUnit: activityUnit,
        order: parseOrder(
          cell(row, layout.mapping, 'activityOrder'),
          category.activities.length + 1,
          rowNumber,
          'activity order',
        ),
        subTasks: [],
        _subTaskMap: new Map(),
      };
      category.activities.push(activity);
      category._activityMap.set(activityKey, activity);
    }

    const subTaskKey = normalizeKey(subTaskName);
    const productivity = parseProductivity(
      cell(row, layout.mapping, 'defaultProductivity'),
      rowNumber,
    );
    const existingSubTask = activity._subTaskMap.get(subTaskKey);

    if (existingSubTask) {
      if (
        existingSubTask.defaultProductivity !== productivity ||
        normalizeKey(existingSubTask.unit) !== normalizeKey(subTaskUnit || activity.defaultUnit)
      ) {
        throw new Error(
          `Row ${rowNumber}: sub-task "${subTaskName}" is repeated with different unit or productivity values.`,
        );
      }
      return;
    }

    const subTask = {
      name: subTaskName,
      description:
        normalizeText(cell(row, layout.mapping, 'subTaskDescription')) || subTaskName,
      defaultProductivity: productivity,
      unit: subTaskUnit || activity.defaultUnit,
      order: parseOrder(
        cell(row, layout.mapping, 'subTaskOrder'),
        activity.subTasks.length + 1,
        rowNumber,
        'sub-task order',
      ),
    };
    activity.subTasks.push(subTask);
    activity._subTaskMap.set(subTaskKey, subTask);
  });

  if (categories.length === 0) {
    throw new Error(`Worksheet "${sheetName}" does not contain any importable rows.`);
  }

  return {
    categories: categories.map(({ _activityMap, _activityCodes, ...category }) => ({
      ...category,
      activities: category.activities.map(({ _subTaskMap, ...activity }) => activity),
    })),
  };
};

export const parseMasterDataWorkbook = (arrayBuffer) => {
  let workbook;
  try {
    workbook = XLSX.read(new Uint8Array(arrayBuffer), {
      type: 'array',
      cellFormula: true,
    });
  } catch (error) {
    throw new Error(`The selected Excel file could not be read: ${error.message}`);
  }

  const errors = [];
  for (const sheetName of workbook.SheetNames) {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      raw: true,
      defval: null,
    });

    try {
      return convertRowsToMasterData(rows, sheetName);
    } catch (error) {
      errors.push(error.message);
    }
  }

  throw new Error(errors[0] || 'No importable worksheet was found.');
};

export const parseMasterDataFile = async (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension === 'json') {
    return parseMasterDataJson(await file.text());
  }
  if (extension === 'xlsx' || extension === 'xls') {
    return parseMasterDataWorkbook(await file.arrayBuffer());
  }

  throw new Error('Choose an Excel (.xlsx or .xls) or JSON (.json) file.');
};
